import { FormEvent, useEffect, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import {
  Badge,
  Button,
  Callout,
  CopyIcon,
  ExternalLinkIcon,
  Label,
  ListEmptyState,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  PencilIcon,
  PlusIcon,
  RefreshIcon,
  SaveIcon,
  Switch,
  TextInput,
  UserPlusIcon,
  XIcon,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { t } from '../../i18n';
import {
  ApiError,
  api,
  type PlatformAdmin,
} from '../../services/api';

export function PlatformAdminsPage() {
  const { user } = useAuth();
  const labels = t().platform;
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(true);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    inviteUrl: string;
    email: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      setAdmins(await api.getPlatformAdmins());
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function openCreate() {
    setEditing(null);
    setName('');
    setEmail('');
    setPassword('');
    setActive(true);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(admin: PlatformAdmin) {
    setEditing(admin);
    setName(admin.name);
    setEmail(admin.email);
    setPassword('');
    setActive(admin.active);
    setFormError(null);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
    setFormError(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!name.trim() || !email.trim()) {
      setFormError(labels.requiredFields);
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await api.updatePlatformAdmin(editing.id, {
          name: name.trim(),
          email: email.trim(),
          active,
        });
        setMessage(labels.adminUpdated);
        closeForm();
        await load();
      } else {
        const result = await api.createPlatformAdmin({
          name: name.trim(),
          email: email.trim(),
          ...(password.trim() ? { password: password.trim() } : {}),
        });
        closeForm();
        setMessage(labels.adminCreated);
        setInviteResult({
          inviteUrl: result.inviteUrl,
          email: result.admin.email,
        });
        setCopied(false);
        await load();
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  async function resendInvite(admin: PlatformAdmin) {
    setResendingId(admin.id);
    setListError(null);
    try {
      const result = await api.recreatePlatformAdminInvite(admin.id);
      setInviteResult({
        inviteUrl: result.inviteUrl,
        email: result.admin.email,
      });
      setCopied(false);
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setResendingId(null);
    }
  }

  async function toggleActive(admin: PlatformAdmin) {
    setListError(null);
    try {
      await api.updatePlatformAdmin(admin.id, { active: !admin.active });
      setMessage(labels.adminUpdated);
      await load();
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  async function copyInviteLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function mailtoInvite(emailAddr: string, url: string) {
    const subject = encodeURIComponent(
      'Πρόσκληση διαχειριστή πλατφόρμας — BalanceFlow',
    );
    const body = encodeURIComponent(
      `Γεια σας,\n\nΣας προσκαλούμε ως διαχειριστή πλατφόρμας στο BalanceFlow.\n\nΑνοίξτε τον σύνδεσμο για να ορίσετε κωδικό και να συνδεθείτε:\n${url}\n\nΟ σύνδεσμος ισχύει για 7 ημέρες.\n`,
    );
    window.open(`mailto:${emailAddr}?subject=${subject}&body=${body}`, '_blank');
  }

  return (
    <PageShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-sm text-muted">{labels.adminsHint}</p>
        <Button type="button" onClick={openCreate}>
          <PlusIcon className="mr-1.5 h-4 w-4" />
          {labels.newAdmin}
        </Button>
      </div>

      {message ? <Callout variant="success">{message}</Callout> : null}
      {listError ? <Callout variant="danger">{listError}</Callout> : null}

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : admins.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-panel/60">
          <ListEmptyState
            title={labels.adminsEmptyTitle}
            hint={labels.adminsEmptyHint}
            action={
              <Button type="button" onClick={openCreate}>
                <PlusIcon className="mr-1.5 h-4 w-4" />
                {labels.newAdmin}
              </Button>
            }
          />
        </div>
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <table className="min-w-full text-left text-sm">
            <thead className={dataTableHeadClass}>
              <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">
                  {labels.colAdminName}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {labels.colAdminEmail}
                </th>
                <th className="px-4 py-3 font-semibold">
                  {labels.colAdminInvite}
                </th>
                <th className="px-4 py-3 font-semibold">{labels.colActive}</th>
                <th className="px-4 py-3 font-semibold">{t().common.actions}</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => {
                const invitePending = Boolean(admin.mustChangePassword);
                const isSelf = user?.id === admin.id;
                return (
                  <tr
                    key={admin.id}
                    className="border-b border-line last:border-0 hover:bg-surface/70"
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink">{admin.name}</span>
                        {isSelf ? (
                          <Badge variant="info">{labels.youBadge}</Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-muted">{admin.email}</td>
                    <td className="px-4 py-3.5">
                      {invitePending ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="warning">{labels.invitePending}</Badge>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            loading={resendingId === admin.id}
                            onClick={() => void resendInvite(admin)}
                          >
                            <RefreshIcon className="h-3.5 w-3.5" />
                            {labels.resendInvite}
                          </Button>
                        </div>
                      ) : (
                        <Badge variant="success">{labels.inviteAccepted}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <Switch
                        checked={admin.active}
                        onChange={() => void toggleActive(admin)}
                        disabled={isSelf}
                        aria-label={labels.colActive}
                      />
                    </td>
                    <td className="px-4 py-3.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(admin)}
                        aria-label={labels.editAdmin}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DataTablePanel>
      )}

      <Modal isOpen={formOpen} onClose={closeForm} size="md">
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <ModalTitle>
              {editing ? labels.editAdmin : labels.newAdmin}
            </ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeForm}
              aria-label={t().common.cancel}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-4">
            {formError ? (
              <Callout variant="danger">{formError}</Callout>
            ) : null}
            <div>
              <Label required>{labels.adminName}</Label>
              <TextInput
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </div>
            <div>
              <Label required>{labels.adminEmail}</Label>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            {!editing ? (
              <div>
                <Label>{labels.adminOptionalPassword}</Label>
                <TextInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-muted">
                  {labels.adminOptionalPasswordHint}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2.5">
                <span className="text-sm">{labels.colActive}</span>
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  disabled={user?.id === editing.id}
                />
              </div>
            )}
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={closeForm}>
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={saving}>
              {editing ? (
                <SaveIcon className="h-4 w-4" />
              ) : (
                <UserPlusIcon className="h-4 w-4" />
              )}
              {editing ? t().common.save : labels.newAdmin}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(inviteResult)}
        onClose={() => setInviteResult(null)}
        size="md"
      >
        <ModalHeader>
          <ModalTitle>{labels.inviteReadyTitle}</ModalTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setInviteResult(null)}
            aria-label={labels.closeInvite}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </ModalHeader>
        <ModalContent className="space-y-4">
          <p className="text-sm text-muted">{labels.inviteReadyHint}</p>
          {inviteResult ? (
            <>
              <div className="rounded-lg border border-line bg-surface/50 px-3 py-3 text-sm">
                <p>
                  <span className="text-muted">{labels.adminEmail}: </span>
                  <span className="font-medium">{inviteResult.email}</span>
                </p>
              </div>
              <div>
                <Label>{labels.inviteLink}</Label>
                <TextInput
                  value={inviteResult.inviteUrl}
                  readOnly
                  showClearButton={false}
                />
              </div>
            </>
          ) : null}
        </ModalContent>
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              inviteResult &&
              mailtoInvite(inviteResult.email, inviteResult.inviteUrl)
            }
          >
            <ExternalLinkIcon className="mr-1.5 h-4 w-4" />
            {labels.sendEmail}
          </Button>
          <Button
            type="button"
            onClick={() =>
              inviteResult && void copyInviteLink(inviteResult.inviteUrl)
            }
          >
            <CopyIcon className="mr-1.5 h-4 w-4" />
            {copied ? labels.copied : labels.copyLink}
          </Button>
        </ModalFooter>
      </Modal>
    </PageShell>
  );
}
